import { COLLECTION_VIEW_QP_KEYS, DEFAULT_PAGE_SIZE } from 'volta/components/collection-view/api';
import { WITHOUT_ATTRIBUTES_ARRAY } from 'volta/components/jsonapi-collection';
import config from 'volta/config/environment';
import { buildCFApiParamPrefix } from 'volta/models/custom-field-definition';

import { isEmpty, isPresent } from '@ember/utils';

import { indexBy, indexByFn } from './array-utils';
import { defaultErrorHandler } from './error-utils';
import { parseNumber } from './math-utils';
import { withoutAttributes } from './object-utils';

import type { TError } from 'volta/utils/error-utils';
import type {
  ICollectionArgs,
  ICollectionFilter,
  ICollectionSort,
  ICollectionViewDefinition,
  ICollectionViewSerialised,
  TCollectionType
} from 'volta/models/types/collection-view';

import type { IJsonApiQuery } from './api/jsonapi-types';
import type { TWorkshopType } from 'volta/models/types/workshop';
import type CollectionView from 'volta/models/collection-view';
import type LabelService from 'volta/services/label-service';
import type { SafeString } from '@ember/template';

type TArrayValue = string | boolean | number;

export const DEFAULT_FILTERS_SEPARATOR: string = config.api.filters.separator;
export const DEFAULT_FILTER_ARRAY_SEPARATOR: string = config.api.filters.arraySeparator;
export const COLLECTION_FILTER_SEPARATOR: string = '~~';
const ARRAY_FILTER_COMPARATORS = new Set(['in', 'ni', 'any', 'all', 'bt']);

function splitBySeparatorOnce(value: string, separator: string): [string, string] {
  const separatorIndex = value.indexOf(separator);
  if (separatorIndex === -1) {
    return [value, ''];
  }
  return [value.slice(0, separatorIndex), value.slice(separatorIndex + separator.length)];
}

function valuesFromFilterExpression(comparator: string, values: string): string[] {
  if (!values) {
    return [];
  }
  return ARRAY_FILTER_COMPARATORS.has(comparator)
    ? values.split(DEFAULT_FILTER_ARRAY_SEPARATOR)
    : [values];
}

export function buildFilterQuery(array: TArrayValue[]) {
  return array.join(DEFAULT_FILTERS_SEPARATOR);
}

/**
 * Serialize an array of values
 * @param {Array<string >} array The array to serialize
 */
export function serializeArrayValues(array: TArrayValue[]): string {
  return array.join(DEFAULT_FILTER_ARRAY_SEPARATOR);
}

export function clearFilter(filter: string) {
  const [, value] = splitBySeparatorOnce(filter, DEFAULT_FILTERS_SEPARATOR);
  return value || filter;
}

/**
 * Create an EQ JSONAPI filter
 */
export function eqFilter(value: TArrayValue) {
  return buildFilterQuery(['eq', value]);
}

export function safeEqFilter(filterAsString: string) {
  return eqFilter(clearFilter(`${filterAsString}`));
}

/**
 * Create an NE JSONAPI filter
 */
export function neFilter(value: TArrayValue) {
  return buildFilterQuery(['ne', value]);
}

/**
 * Create an GT JSONAPI filter
 */
export function gtFilter(value: TArrayValue) {
  return buildFilterQuery(['gt', value]);
}

/**
 * Create an LT JSONAPI filter
 */
export function ltFilter(value: TArrayValue) {
  return buildFilterQuery(['lt', value]);
}

/**
 * Create an BT JSONAPI filter
 */
export function btFilter(array: TArrayValue[]) {
  return buildFilterQuery(['bt', serializeArrayValues(array)]);
}

/**
 * Create an LK JSONAPI filter
 */
export function lkFilter(value: TArrayValue) {
  return buildFilterQuery(['lk', value]);
}

/**
 * Create an IN JSONAPI filter
 */
export function inFilter(array: string[]) {
  return buildFilterQuery(['in', serializeArrayValues(array)]);
}

/**
 * Safely create an IN JSONAPI filter from a string  formatted filter
 */
export function safeInFilter(filterAsString: string) {
  return inFilter(clearFilter(`${filterAsString}`).split(DEFAULT_FILTER_ARRAY_SEPARATOR));
}

export function niFilter(array: string[]) {
  return buildFilterQuery(['ni', serializeArrayValues(array)]);
}

export function anyFilter(array: string[]) {
  return buildFilterQuery(['any', serializeArrayValues(array)]);
}

export function safeAnyFilter(filterAsString: string) {
  return anyFilter(clearFilter(filterAsString).split(DEFAULT_FILTER_ARRAY_SEPARATOR));
}

export function allFilter(array: string[]) {
  return buildFilterQuery(['all', serializeArrayValues(array)]);
}

export function safeAllFilter(filterAsString: string) {
  return allFilter(clearFilter(`${filterAsString}`).split(DEFAULT_FILTER_ARRAY_SEPARATOR));
}

export function serializeFilters(
  filters: Record<string, string>,
  filterDefinitions: TFilterDefinition[]
) {
  let customFields: object | null = null;
  const queryParams = {} as {
    [key: string]: string | undefined;
  };

  filterDefinitions.forEach((filterDef) => {
    const paramKey = filterDef.queryParam;

    if (Object.prototype.hasOwnProperty.call(filters, paramKey)) {
      const currentFilter = filters[paramKey];

      if (currentFilter && currentFilter.length > 0) {
        if (paramKey.includes(buildCFApiParamPrefix())) {
          const cfKeys = paramKey.split(buildCFApiParamPrefix());
          const cfKey = cfKeys.at(-1);

          if (cfKey) {
            customFields = {
              ...customFields,
              [cfKey]: currentFilter
            };
          }
        } else {
          queryParams[paramKey] = currentFilter;
        }
      } else {
        if (!paramKey.includes(buildCFApiParamPrefix())) {
          queryParams[paramKey] = undefined;
        }
      }
    }
  });

  if (customFields) {
    queryParams['customFields'] = customFields;
  }

  return queryParams;
}

/**
 * [serializeFilterValues Transform filter object into Query Params]
 */
export function serializeFilterValues(
  filterValues: TFilterValues,
  filterDefinitions: TFilterDefinition[]
): TQueryParams {
  let customFields: object | null = null;
  const queryParams = {} as {
    [key: string]: string | undefined;
  };

  filterDefinitions.forEach((filterDef) => {
    const paramKey = filterDef.queryParam;

    if (Object.prototype.hasOwnProperty.call(filterValues, paramKey)) {
      const currentFilter = filterValues[paramKey];

      if (currentFilter && currentFilter.enabled && currentFilter.values.length > 0) {
        const filterValue = `${
          currentFilter.comparator
        }${DEFAULT_FILTERS_SEPARATOR}${currentFilter.values.join(DEFAULT_FILTER_ARRAY_SEPARATOR)}`;
        if (paramKey.includes(buildCFApiParamPrefix())) {
          const cfKeys = paramKey.split(buildCFApiParamPrefix());
          const cfKey = cfKeys.at(-1);
          if (cfKey) {
            customFields = {
              ...customFields,
              [cfKey]: filterValue
            };
          }
        } else {
          queryParams[paramKey] = filterValue;
        }
      } else {
        if (!paramKey.includes(buildCFApiParamPrefix())) {
          queryParams[paramKey] = undefined;
        }
      }
    }
  });

  if (customFields) {
    queryParams['customFields'] = customFields;
  }

  return queryParams;
}

export function resetFilterValues(filterDefinitions: TFilterDefinition[]) {
  const resetFilters = {} as { [key: string]: TFilterValue | undefined };

  filterDefinitions.forEach((obj) => {
    const { clearDefaultValue, queryParam } = obj;

    resetFilters[queryParam] = clearDefaultValue
      ? deserializeFilterValue(serializeArrayValues(clearDefaultValue), obj)
      : undefined;
  });

  return resetFilters;
}

export function filtersCount(filterValues: TFilterValues = {}, defaultFilters: string[] = []) {
  return Object.keys(filterValues).filter(
    (key) => filterValues[key] && filterValues[key]?.enabled && !defaultFilters.includes(key)
  ).length;
}

/**
 * Parses the filter values sent by the router and builds a new object
 * to be sent to the filter list component.
 */
export function deserializeFilterValue(
  filterValue: string | null | undefined,
  filterDefinition?: TFilterDefinition
): TFilterValue | undefined {
  const fallbackComparator = filterDefinition?.comparators.at(0) ?? 'in';
  const asString = filterValue != null ? `${filterValue}` : '';
  const [comparator, rawValues] = asString.includes(DEFAULT_FILTERS_SEPARATOR)
    ? splitBySeparatorOnce(asString, DEFAULT_FILTERS_SEPARATOR)
    : [fallbackComparator, asString];

  return {
    comparator: comparator as TComparator,
    values: valuesFromFilterExpression(comparator, rawValues),
    enabled: true
  };
}

export function getFilterValuesFromQueryParams(
  params: TQueryParams,
  definitions: TFilterDefinition[]
) {
  const values = {} as Record<string, any>;

  let { customFields = {} } = params;

  try {
    customFields =
      typeof customFields === 'object' ? customFields : JSON.parse(customFields as string);
  } catch (error) {
    defaultErrorHandler(error as TError);
  }

  definitions.forEach((definition) => {
    const paramKey = definition.queryParam;
    if (paramKey.includes(buildCFApiParamPrefix())) {
      // Manage custom fields filters
      const cfKeys = paramKey.split(buildCFApiParamPrefix());
      const cfKey = cfKeys.at(-1);
      const filter = customFields?.[cfKey as keyof typeof customFields];

      if (filter) {
        values[paramKey] = deserializeFilterValue(filter, definition);
      }
    } else {
      // Manage normal filters
      const param = params[paramKey];
      if (param) {
        values[paramKey] = deserializeFilterValue(param, definition);
      }
    }
  });

  return values;
}

export function collectionViewFromQueryParams(
  qp: TQueryParams,
  collectionDefinition: ICollectionViewDefinition
): Partial<ICollectionViewSerialised> {
  const { pageSize, filters, sortBy, groupBy, v } = qp;
  let { args, collectionType } = qp;

  try {
    args = args ? JSON.parse(args) : undefined;
  } catch {
    args = undefined;
  }

  if (collectionType && !collectionDefinition.types.includes(collectionType as TCollectionType)) {
    collectionType = collectionDefinition.defaultView.collectionType;
  }

  return {
    id: v ?? 'default',
    filters: filters ? collectionFiltersFromQueryParams(filters) : undefined,
    sortBy: sortBy ? collectionSortByFromQueryParams(sortBy) : undefined,
    groupBy: groupBy ? stringListToArray(groupBy) : undefined,
    args: args as ICollectionArgs,
    pageSize: pageSize ? parseNumber(pageSize) : DEFAULT_PAGE_SIZE,
    collectionType: collectionType as TCollectionType
  };
}

type TArgsWithContent = ICollectionArgs & { content?: unknown };

export function collectionViewToQueryParams<TArgs extends TArgsWithContent = TArgsWithContent>(
  context: {
    id?: string | null;
    filters?: ICollectionFilter[];
    sortBy?: ICollectionSort[];
    groupBy?: string[];
    pageSize?: number;
    args?: TArgs;
    collectionType?: TCollectionType;
  },
  queryParams: TQueryParams,
  collectionDefinition: ICollectionViewDefinition
): TQueryParams {
  const { page } = queryParams;
  const { filters, sortBy, pageSize, groupBy, args, id, collectionType } = context;

  const filtersDefsByProp = indexBy(collectionDefinition.filters, 'queryParam');
  let cvSortBy: ICollectionSort[] = sortBy ?? [];
  const cvFilters = indexByFn(filters as ICollectionFilter[], (f) => f.property);

  const newQP = withoutAttributes(
    queryParams,
    WITHOUT_ATTRIBUTES_ARRAY.concat(COLLECTION_VIEW_QP_KEYS)
  );

  for (const key of Object.keys(newQP)) {
    if (key === 'page' || key === 'v') {
      continue;
    } else if (key === 'sort' && queryParams[key]) {
      cvSortBy = collectionSortByFromQueryParams(queryParams[key]);
    } else {
      const { comparator, values, enabled } = deserializeFilterValue(
        newQP[key]!,
        filtersDefsByProp[key]
      ) as TFilterValue;
      if (enabled && values.length > 0) {
        cvFilters[key] = { comparator, values: values, property: key };
      }
    }
    newQP[key] = undefined;
  }

  const undefinedIfEmpty = (str?: any) => (isEmpty(str) ? undefined : `${str}`);

  const qpPage = `${parseNumber(page) ?? 1}`;
  const qpPageSize = `${parseNumber(queryParams['pageSize']) ?? DEFAULT_PAGE_SIZE}`;

  const cv = {
    ...newQP,
    v: id === 'default' ? undefined : id,
    page,
    pageSize: undefinedIfEmpty(pageSize ?? DEFAULT_PAGE_SIZE),
    filters: undefinedIfEmpty(collectionFiltersToQueryParams(Object.values(cvFilters))),
    groupBy: undefinedIfEmpty(serializeArrayValues(groupBy ?? [])),
    sortBy: undefinedIfEmpty(collectionSortByToQueryParams(cvSortBy)),
    args: undefinedIfEmpty(JSON.stringify(args?.content ?? args ?? {})),
    collectionType: undefinedIfEmpty(
      collectionType ?? collectionDefinition.defaultView.collectionType
    )
  };

  if (
    qpPage !== '1' &&
    (cv.sortBy !== queryParams['sortBy'] ||
      cv.pageSize !== qpPageSize ||
      cv.filters !== queryParams['filters'] ||
      cv.args !== queryParams['args'] ||
      cv.groupBy !== queryParams['groupBy'])
  ) {
    cv.page = '1';
  }

  return cv;
}

export function getFiltersParamsFromCollectionView(cv: CollectionView | ICollectionViewSerialised) {
  const filters: Record<string, string> = {};

  const buildFilter = (filter: ICollectionFilter) =>
    buildFilterQuery([filter.comparator, serializeArrayValues(filter.values)] as TArrayValue[]);

  if (cv.filters) {
    cv.filters.forEach((filter) => {
      const { property } = filter;
      filters[property] = buildFilter(filter);
    });
  }
  return filters;
}

export function getFiltersFromQueryParams(
  params: TQueryParams,
  definitions: TFilterDefinition[],
  apiPrefix?: string
) {
  const filters: Record<string, string> = {};
  const { customFields = {} } = params;
  const cfApiPrefix = buildCFApiParamPrefix(apiPrefix);
  for (const definition of definitions) {
    const paramKey = definition.queryParam;

    if (paramKey.includes(cfApiPrefix)) {
      // Manage custom fields filters
      const cfKeys = paramKey.split(cfApiPrefix);
      const cfKey = cfKeys.at(-1);
      const filter = customFields?.[cfKey as keyof typeof customFields];

      if (filter) {
        filters[definition.apiParam] = filter;
      }
    } else {
      // Manage normal filters
      if (params[paramKey]) {
        filters[definition.apiParam] = params[paramKey] as string;
      }
    }
  }

  return filters;
}

export function getStrictFiltersFromQueryParams(
  params: TQueryParams,
  definitions: TFilterDefinition[]
) {
  const filters: Record<string, string> = {};

  for (const definition of definitions) {
    const paramKey = definition.queryParam;

    if (params[paramKey]) {
      filters[definition.apiParam] = params[paramKey] as string;
    }
  }

  return filters;
}

/**
 * Utility function for calculating an offset from a page number and limit
 */
export function offsetFromPage(page: number, limit: number) {
  // avoid page numbers to be trolled i.e.: page=string , page=-1, page=1.23
  page = isNaN(page) ? 1 : Math.floor(Math.abs(page));
  // page=1 will result into offset 0, page=2 will result into offset 10 and so on
  return (page - 1) * limit;
}

/**
 * Utility function for calculating the page limit of a API collection
 */
export function pageLimit(pageSize: number) {
  return Math.floor(Math.abs(pageSize));
}

/**
 * Converts a string  to an array of values
 *
 * @param {string } str A string  value formatted as an array
 * @returns {string []} An array of values
 */
export function stringListToArray(str?: string | null): string[] {
  return isPresent(str) ? str!.split(DEFAULT_FILTER_ARRAY_SEPARATOR) : [];
}

export function collectionFiltersFromQueryParams(str?: string): ICollectionFilter[] {
  const filterStrings = str ? str.split(COLLECTION_FILTER_SEPARATOR) : [];
  return filterStrings.reduce<ICollectionFilter[]>((acc, fs) => {
    const [key, comparatorAndValues] = splitBySeparatorOnce(fs, DEFAULT_FILTERS_SEPARATOR);
    const [comparator, values] = splitBySeparatorOnce(
      comparatorAndValues,
      DEFAULT_FILTERS_SEPARATOR
    );
    if (values && values.length > 0 && key) {
      acc.push({
        comparator: comparator as TComparator,
        property: key,
        values: valuesFromFilterExpression(comparator, values)
      });
    }
    return acc;
  }, []);
}

export function collectionFiltersQueryParamsToQueryParams(filters?: string | null): TQueryParams {
  const qp: TQueryParams = {};
  const filterStrings = filters ? filters.split(COLLECTION_FILTER_SEPARATOR) : [];
  filterStrings.forEach((fs) => {
    const [key, comparatorAndValues] = splitBySeparatorOnce(fs, DEFAULT_FILTERS_SEPARATOR);
    const [comparator, values] = splitBySeparatorOnce(
      comparatorAndValues,
      DEFAULT_FILTERS_SEPARATOR
    );

    if (key && comparator && values?.length && key) {
      qp[key] = `${comparator}${DEFAULT_FILTERS_SEPARATOR}${values}`;
    }
  });
  return qp;
}

export function collectionFiltersFromJsonApiFilters(
  filters: IJsonApiQuery['filter'] = {},
  defs: TFilterDefinition[] = []
) {
  const indexedColumnDefs = indexByFn(defs, (d) => d.apiParam);
  return Object.keys(filters).reduce<ICollectionFilter[]>((acc, key) => {
    const [comparator, values] = splitBySeparatorOnce(`${filters[key]}`, DEFAULT_FILTERS_SEPARATOR);

    if (values.length) {
      acc.push({
        comparator: comparator as TComparator,
        property: indexedColumnDefs[key]?.queryParam ?? key,
        values: valuesFromFilterExpression(comparator, values)
      });
    }
    return acc;
  }, []);
}

export function filterValuesToQueryParams(filters: TFilterValues): string {
  return Object.entries(filters)
    .reduce<string[]>((acc, [key, f]) => {
      if (f && f.values.length > 0) {
        acc.push(
          `${key}${DEFAULT_FILTERS_SEPARATOR}${f.comparator}${DEFAULT_FILTERS_SEPARATOR}${serializeArrayValues(f.values)}`
        );
      }
      return acc;
    }, [])
    .join(COLLECTION_FILTER_SEPARATOR);
}

export function filterValuesFromQueryParams(str?: string | null): TFilterValues {
  const filterStrings = str ? str.split(COLLECTION_FILTER_SEPARATOR) : [];
  return filterStrings.reduce<TFilterValues>((acc, fs) => {
    const [key, comparatorAndValues] = splitBySeparatorOnce(fs, DEFAULT_FILTERS_SEPARATOR);
    const [comparator, values] = splitBySeparatorOnce(
      comparatorAndValues,
      DEFAULT_FILTERS_SEPARATOR
    );
    if (values.length > 0 && key) {
      acc[key] = {
        enabled: true,
        comparator: comparator as TComparator,
        values: valuesFromFilterExpression(comparator, values)
      };
    }
    return acc;
  }, {});
}

export function collectionFiltersToQueryParams(filters: ICollectionFilter[]): string {
  return filters
    .reduce<string[]>((acc, f) => {
      if (f.values.length > 0) {
        acc.push(
          `${f.property}${DEFAULT_FILTERS_SEPARATOR}${f.comparator}${DEFAULT_FILTERS_SEPARATOR}${serializeArrayValues(f.values)}`
        );
      }
      return acc;
    }, [])
    .join(COLLECTION_FILTER_SEPARATOR);
}

export function collectionSortByFromQueryParams(str?: string | null): ICollectionSort[] {
  return stringListToArray(str).map((sb) => {
    const prop = sb.split('-');
    return { property: prop.at(-1), asc: prop.length === 1 } as ICollectionSort;
  });
}

export function collectionSortByToQueryParams(str: ICollectionSort[] = []): string {
  return serializeArrayValues(str.map((s) => `${s.asc ? '' : '-'}${s.property}`));
}

export function beforeQueryWorkshop(
  workshopTypes: TWorkshopType[],
  warehouseFilter?: string | null,
  strict?: boolean
) {
  return (query: IJsonApiQuery) => {
    const filter = query.filter ?? {};
    if (workshopTypes.length) {
      filter[strict ? 'workshopTypesStrict' : 'workshopTypes'] = inFilter(workshopTypes);
    }

    if (warehouseFilter) {
      filter['warehouseId'] = safeEqFilter(warehouseFilter);
    }

    return { ...query, filter };
  };
}

/**
 * Translate the label of the filter options
 */
export function translateFilterOptions<T extends { label?: string | SafeString }>(
  options: T[],
  labelService: LabelService
): T[] {
  return options.map((option) => {
    return { ...option, label: option.label ? labelService.tOrElse(option.label) : undefined };
  });
}
